import { PaymentPlan, Subscription } from 'generated/api';
import moment from 'moment-timezone';

class SubscriptionUtils {
  activeSubscriptionUntilThisMonth(sub: Subscription, date: number) {
    let { created, ended_at, cancel_at } = sub;
    let startOfPeriod = moment.unix(date).startOf('month').unix();
    if (ended_at! > 0 || cancel_at! > 0) {
      let endDate = Math.max(ended_at || 0, cancel_at || 0);
      return created! <= startOfPeriod && endDate > date;
    } else {
      return created! <= startOfPeriod;
    }
  }

  convertPlanAmount(amount: number) {
    return (amount / 100.0).toFixed(2);
  }

  isActiveSubscription(subscription: Subscription | undefined) {
    return subscription?.status === 'trialing' || subscription?.status === 'active';
  }

  isPaymentOverdue(subscription: Subscription | undefined) {
    return subscription?.status === 'past_due' || subscription?.status === 'unpaid';
  }

  isUnsubscribed(subscription: Subscription | undefined) {
    return !subscription || subscription.id === 'unsubscribed';
  }

  /**
   * Plans that unlock practitioner skills (e.g. Executive Health) get priority treatment
   * in staff queues — a distinct badge colour and a spot at the top of the queue.
   */
  isPlanGated(subscription?: Subscription | null) {
    if (this.isUnsubscribed(subscription ?? undefined)) return false;
    const skills = subscription?.plan?.unlocks_skills;
    return Array.isArray(skills) && skills.length > 0;
  }

  isScheduledToCancel(subscription: Subscription | undefined) {
    return subscription && (subscription.cancel_at_period_end === true || subscription.cancel_at);
  }

  getSubscriptionName(subscription: Subscription | undefined) {
    return subscription?.plan?.name ?? 'N/A';
  }

  getInterval(subscription: Subscription | undefined) {
    const interval = subscription?.plan?.interval;
    return interval ? `${interval}ly` : '';
  }

  isActivePayingSubscription(subscription: Subscription | undefined) {
    return !this.isUnsubscribed(subscription) && this.isActiveSubscription(subscription);
  }

  getMinimumMonths(plan: PaymentPlan | undefined) {
    const minimumMonths = plan?.minimum_months ?? 0;
    return minimumMonths > 0 ? minimumMonths : 0;
  }

  minimumMonthsEndDate(subscription: Subscription | undefined) {
    if (!subscription || !subscription.plan) return undefined;
    const minimumMonths = this.getMinimumMonths(subscription.plan);
    if (!minimumMonths) return undefined;
    let startDate = subscription.created!;
    return moment.unix(startDate).add(minimumMonths, 'months').unix();
  }

  stillInMinimumMonths(subscription: Subscription | undefined) {
    let date = this.minimumMonthsEndDate(subscription);
    if (!date) return false;
    return moment().isBefore(moment.unix(date));
  }
}

export default new SubscriptionUtils();
